/**
 * The whole stylesheet for the generated event pack: page geometry and content styling both.
 *
 * It is ALL of it because the pack exports with `onlyExtraCss` - paged.js measures the page boxes
 * it builds, and the app's own globals (Ionic's resets above all) distort those measurements badly
 * enough to halt its layout loop, which yields a PDF of one or two blank pages instead of the
 * document. The rulebook learnt this the same way; see PdfExportOptions.onlyExtraCss.
 *
 * A bundled string rather than an asset file, which is where this differs from the rulebook. The
 * rulebook fetches `/assets/styles/rulebook-print.css` because an earlier angular.json bundle
 * resolved under `ng build` and not under `ng serve`. An asset cannot work here at all: this page
 * lives in army-builder-shared and is served by whichever app hosts it, so there is no single
 * asset path that resolves for every one. A template literal is compiled into the bundle and needs
 * no fetch, so it cannot 404 and cannot differ between serve and build.
 *
 * The `@page` rules are deliberately not linked into the app document - they would change the
 * browser's own Ctrl+P on every page of the app while doing nothing on screen, and
 * PdfExportService strips `@page` from collected stylesheets anyway. `extraCss` is exempt.
 */
export const EVENT_PACK_CSS = `
@page {
    size: A4;
    margin: 18mm 16mm 16mm;

    /* The folio and the generated-on date sit on every sheet. A printed pack outlives the data it
       was made from, so a reader holding one needs to see how old it is without being told. */
    @bottom-left {
        content: string(packfooter);
        font-size: 7.5pt;
        color: #6b7783;
    }

    @bottom-right {
        content: counter(page);
        font-size: 8pt;
        color: #6b7783;
    }
}

/* A cover carries no folio and no footer, by document convention. */
@page :first {
    @bottom-left {
        content: none;
    }
    @bottom-right {
        content: none;
    }
}

body {
    margin: 0;
    font-family: Helvetica, Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.45;
    color: #1d2329;
}

/* Carries the footer text into the @page margin box above. Hidden in its own right: it exists to
   be read by string-set, not to be printed where it sits. */
.pack-footer-source {
    string-set: packfooter content(text);
    position: absolute;
    height: 0;
    overflow: hidden;
    font-size: 1pt;
    color: transparent;
}

.pack-page {
    /* Every section after the first opens a sheet. The schedule getting its own page is this rule
       and nothing more. */
    break-before: page;
}

.pack-page:first-of-type {
    break-before: auto;
}

h1 {
    margin: 0 0 4mm;
    font-size: 22pt;
    line-height: 1.15;
}

h2 {
    margin: 0 0 3mm;
    font-size: 14pt;
    border-bottom: 0.4mm solid #1d2329;
    padding-bottom: 1.5mm;
}

h3 {
    margin: 4mm 0 1.5mm;
    font-size: 11pt;
}

p {
    margin: 0 0 2.5mm;
}

/* The organiser's prose is markdown by the time it prints, so no white-space handling here: the
   renderer's "breaks" option already turned their line breaks into real <br> elements, and
   pre-wrap would additionally preserve the newlines marked emits between its own tags.
   (No backticks anywhere in this string - it is a template literal.) */
.markdown-body > :first-child {
    margin-top: 0;
}

.markdown-body > :last-child {
    margin-bottom: 0;
}

.markdown-body p {
    margin: 0 0 2.5mm;
}

.markdown-body ul,
.markdown-body ol {
    margin: 0 0 2.5mm;
    padding-left: 6mm;
}

.markdown-body li {
    margin-bottom: 0.8mm;
}

.markdown-body h2,
.markdown-body h3,
.markdown-body h4 {
    margin: 3mm 0 1.5mm;
    font-size: 11pt;
    border: 0;
    padding: 0;
}

/* Dark rather than the browser's blue: this is read on paper as often as on screen, and the
   underline alone marks it. The href survives either way, so it stays clickable in the PDF. */
.markdown-body a {
    color: #1d2329;
    text-decoration: underline;
}

.markdown-body blockquote {
    margin: 0 0 2.5mm;
    padding-left: 3mm;
    border-left: 0.5mm solid #8d97a2;
    color: #3f4a55;
}

/* A list must not be split mid-item across a sheet. */
.markdown-body li,
.markdown-body blockquote {
    break-inside: avoid;
}

.pack-cover-meta {
    margin: 0 0 8mm;
    font-size: 11pt;
    color: #3f4a55;
}

/* The cover is mostly white space, so the title gets room above it rather than sitting hard
   against the top margin like a continuation page. */
.pack-cover {
    padding-top: 24mm;
}

.pack-cover h1 {
    border-bottom: 0.6mm solid #1d2329;
    padding-bottom: 4mm;
}

/* Away from the prose it would otherwise look like a continuation of. */
.pack-cover-updated {
    margin-top: 10mm;
    font-size: 9pt;
}

.pack-rows {
    width: 100%;
    border-collapse: collapse;
}

.pack-rows th,
.pack-rows td {
    text-align: left;
    vertical-align: top;
    padding: 1.4mm 0;
    border-bottom: 0.2mm solid #d7dce1;
}

.pack-rows th {
    width: 45%;
    font-weight: 600;
}

/* A row must not be split across a sheet boundary - half a label on one page and its value on the
   next is worse than a short page. */
.pack-rows tr,
.pack-schedule-block,
.pack-address {
    break-inside: avoid;
}

.pack-schedule-block {
    margin: 0 0 4mm;
    padding-left: 3mm;
    border-left: 0.6mm solid #8d97a2;
}

/* The time, the block's name and its length sit on one line with real space between them.
   Without this they run together and a round reads "09:00Round 1150 min". */
.pack-schedule-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0 4mm;
}

/* Fixed width so every block's name starts at the same place down the page, which is what makes
   the column scannable rather than ragged. */
.pack-schedule-time {
    flex: 0 0 18mm;
    font-weight: 600;
}

.pack-schedule-label {
    font-weight: 600;
}

.pack-schedule-description {
    margin: 1mm 0 0;
    color: #3f4a55;
}

/* Each section is a heading plus its prose; keeping them together stops a heading being
   orphaned at the foot of a sheet with its text overleaf. */
.pack-info-section {
    margin: 0 0 5mm;
    break-inside: avoid;
}

.pack-info-section h3 {
    margin-top: 0;
}

.pack-address {
    margin: 0 0 4mm;
    font-style: normal;
    line-height: 1.5;
}

.pack-muted {
    color: #6b7783;
}
`;
